import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, Input, signal } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { SidenavStore } from 'src/app/services/stores/sidenav.store';
import { SpinnerComponent, TreeNode } from 'ui-library';
@Component({
  selector: 'app-nested-navigation',
  imports: [
    MatIconModule,
    RouterLinkActive,
    CommonModule,
    TranslateModule,
    MatDivider,
    SpinnerComponent,
    MatTooltipModule
  ],
  standalone: true,
  templateUrl: './nested-navigation.component.html',
  styleUrl: './nested-navigation.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden'
      })),
      state('expanded', style({
        height: '*',
        opacity: 1,
        overflow: 'hidden'
      })),
      transition('collapsed <=> expanded', [
        animate('180ms cubic-bezier(0.4,0,0.2,1)')
      ])
    ]),
    trigger('fadeSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate(
          '160ms cubic-bezier(0.4,0,0.2,1)',
          style({ opacity: 1, transform: 'none' })
        )
      ]),
      transition(':leave', [
        animate(
          '120ms cubic-bezier(0.4,0,0.2,1)',
          style({ opacity: 0, transform: 'translateY(-8px)' })
        )
      ])
    ])
  ]
})

export class NestedNavigationComponent {
  // Services
  private readonly router = inject(Router);
  private readonly SidenavStore = inject(SidenavStore);
  private readonly uiTool = inject(UiTool);
  private readonly dashboardStore = inject(DashboardStore);

  // Component API
  @Input() nodes: TreeNode[] = [];
  @Input() open: boolean = true;

  // Signals
  private currentUrlSignal = signal(this.router.url);

  // Computeds
  readonly activeRouteMap = computed<Record<string, boolean>>(() => {
    const url = this.currentUrlSignal();
    const dashboardId = this.dashboardStore.currentDashboardId();

    const map: Record<string, boolean> = {};

    for (const node of this.nodes) {
      if (!node.route) continue;

      if (node.route.includes('dashboardId=')) {
        const params = new URLSearchParams(node.route.split('?')[1]);
        const nodeDashboardId = params.get('dashboardId');
        map[node.route] = url.startsWith('/project/dashboard') && nodeDashboardId === dashboardId;

        continue;
      }

      const cleanRoute = node.route.startsWith('/') ? node.route.slice(1) : node.route;
      const segments = ['project', ...cleanRoute.split('/')];
      const tree = this.router.createUrlTree(segments);

      map[node.route] = this.router.isActive(tree, {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
      });
    }

    return map;
  });

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrlSignal.set(this.router.url);
      });
  }

  isRouteActive(route?: string): boolean {
    if (!route) return false;
    return !!this.activeRouteMap()[route];
  }

  toggleNode(node: TreeNode) {
    if (node.children?.length) {
      node.expanded = !node.expanded;
    }
  }

  toggleFullNode(node: TreeNode) {
    if (node.children && node.children.length >= 6) {
      node.fullyExpanded = !node.fullyExpanded;
    }
  }

  closeSidenav() {
    if (this.uiTool.isMobile())
      this.SidenavStore.closeSidenav();
  }

  navigateToRoute(route: string) {
    this.closeSidenav();

    const [path, queryString] = route.split('?');
    const fullPath = path && path.startsWith('/') ? path : `/project/${path ?? ''}`;

    if (!queryString) {
      this.router.navigate([fullPath]);
      return;
    }

    const queryParams: Record<string, string> = {};
    queryString.split('&').forEach(p => {
      const [k, v] = p.split('=');
      if (k && v) queryParams[k] = decodeURIComponent(v);
    });

    this.router.navigate([fullPath], { queryParams });
  }

  trackByNode(index: number, node: TreeNode) {
    return node.name ?? node.route ?? index;
  }
}
